import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, finalize, from, map, Observable, of, Subject } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { LoggedUser } from './login-info.interface';
import { MsalService } from '@azure/msal-angular';
import { ConfirmationService } from 'primeng/api';
import { LoggerService } from '../logger-service/logger.service';
import { Art57Section } from '../../ui/Art57Section/Art57Section';
import { LookupGetService } from '../lookup-service/lookup-get.service';

@Injectable({
  providedIn: 'root'
})

export class AuthService {

  loadingUser!: boolean;
  org_type: string = "";
  org_name: string = "";
  selectedSection: string = "";
  private beforeUnloadHandler = this.handleBeforeUnload.bind(this);


  constructor(private httpClient: HttpClient,
    private msalService: MsalService,
    private confirmationService: ConfirmationService,
    private loggerService: LoggerService,
    private injector: Injector
  ) {
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  public userBehaviorSubject!: BehaviorSubject<LoggedUser>;
  //public userBehaviorSubject: BehaviorSubject<LoggedUser> = new BehaviorSubject<LoggedUser>(this.getPrincipal()); //This would solve the issue for Angular extension in the browser Uncaught TypeError: Cannot read properties of undefined (reading 'value') at get user() (auth-service.service.ts)

  get user(): LoggedUser {
    return this.userBehaviorSubject.value;
  }

  setSelectedSection(selectedSection: string) {
    this.selectedSection = selectedSection;
  }

  updatedDataSelection(user: LoggedUser): void {
    this.loadingUser = false;
    this.userBehaviorSubject.next(user);
  }

  /**
 * @name existsPermission
 * @description
 * Returns whether the user has the permissions provided.
 * @returns boolean
 * @memberof AuthService
 */
  existsPermission(): boolean {
    return this.user.permissions.length > 0;
  }


  /**
   * @name setOrganisationProfileInfo
   * @description
   * set organisation
   * @returns
   * @memberof AuthService
   */
  setOrganisationAndPermissions(organisationId: string): Observable<LoggedUser> {
    return this.httpClient.post<LoggedUser>(
      `${environment.apiUrl}auth/organisations/${organisationId}`, null
    );
  }

  getPrincipal(): LoggedUser {
    const user: LoggedUser = JSON.parse(sessionStorage.getItem('principal')!) as LoggedUser;
    return user;
  }

  private loginCompleted = new BehaviorSubject<LoggedUser>(this.getPrincipal());
  getLoginCompleted(): BehaviorSubject<LoggedUser> {
    return this.loginCompleted;
  }

  private logoutCompleted = new BehaviorSubject<string>("");
  getLogoutCompleted(): BehaviorSubject<string> {
    return this.logoutCompleted;
  }

  private sessionExpiredCompleted = new BehaviorSubject<string>("");
  getSessionExpired(): BehaviorSubject<string> {
    return this.sessionExpiredCompleted;
  }

  private organisationSwitchCompletedSubject = new Subject<void>();
  organisationSwitchCompleted$: Observable<void> = this.organisationSwitchCompletedSubject.asObservable();

  handleBeforeUnload(event: BeforeUnloadEvent) {
    if (this.getSessionToken()) {
      try {
        fetch(environment.apiUrl + "/api/authorisation/logout", {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${this.getAccessToken()}`, 'AuthSession': `${this.getSessionToken()}` },
          keepalive: true
        }).catch(() => { /* best-effort */ });
      } catch (e) {
        this.loggerService.warn('fetch keepalive not available', e);
      }
    }
  }

  public logOut(): void {
    if (this.getSessionToken()) {
      const headers = new HttpHeaders({
        Authorization: 'Bearer ' + this.getAccessToken(),
        AuthSession: this.getSessionToken()
      });

      this.httpClient
        .post(
          `${environment.apiUrl}/api/authorisation/logout`,
          null,
          { headers }
        )
        .pipe(
          finalize(() => {
            this.logOutCompleted();
          })
        )
        .subscribe();
    } else {
      this.logOutCompleted();
    }
  }

  switchOrganisation(): void {
    if (this.getSessionToken()) {
      const headers = new HttpHeaders({
        Authorization: 'Bearer ' + this.getAccessToken(),
        AuthSession: this.getSessionToken()
      });

      this.httpClient
        .post(
          `${environment.apiUrl}/api/authorisation/switch-organisation`,
          null,
          { headers }
        )
        .pipe(
          finalize(() => {
            this.cleanSession();
          })
        )
        .subscribe();
    } else {
      this.cleanSession();
    }
  }

  private cleanSession(){
                //Remove session data and clear cache after switching organisation
            this.removeSessionData();
            //Remove cache data from LookupGetService after switching organisation
            this.injector.get(LookupGetService).clearCache();
            //Notify that the organisation switch has been completed
            this.organisationSwitchCompletedSubject.next();
  }

  private logOutCompleted(): void {
    sessionStorage.clear();
    this.msalService.logoutRedirect();
  }

  public logOutConfirm(): void {
    this.confirmationService.close(); //close previous dialog if there was opened any.
    this.confirmationService.confirm({
      key: 'sessionLogoutDialog',
      header: 'Please confirm you wish to logout',
      message: 'All current unsaved data will be lost.',
      icon: 'pi pi-question-circle',
      acceptLabel: 'Yes, Logout',
      rejectLabel: 'Cancel',
      accept: () => {
        this.logOut();
      },
      reject: () => { }
    });
  }

  public switchOrganisationConfirm(): void {
    this.confirmationService.confirm({
      header: 'Please confirm you wish to log with another organisation',
      message: 'All current unsaved data will be lost.',
      icon: 'pi pi-question-circle',
      acceptLabel: 'Yes, log in',
      rejectLabel: 'Cancel',
      accept: () => {
        this.switchOrganisation();
      },
      reject: () => { }
    });
  }

  sessionExpired() {
    sessionStorage.clear();
    this.logoutCompleted.next("sessionexpired");
    this.sessionExpiredCompleted.next("sessionexpired");
  }

  private removeSessionData(): void {
    sessionStorage.removeItem('principal');
    sessionStorage.removeItem('organisationId');
    sessionStorage.removeItem('orgType');
    sessionStorage.removeItem('orgName');
    sessionStorage.removeItem('sessionToken');
    
  }

  resetPrincipal() {
    this.removeSessionData();
    this.logoutCompleted.next("logout");
  }

  initializeSessionWithPrincipal(principal: LoggedUser) {
      this.loggerService.debug('Requesting to initialize Session with the principal and set organisation.');
      sessionStorage.setItem('principal', JSON.stringify(principal));
      this.setSessionToken(principal.accessToken);
      this.setOrganisation(principal.organisation.code);
      // not required this action is done in section-container.component -->  this.injector.get(Art57Section).initSections();
      this.loggerService.debug('Successfully initialized Session with the principal and set organisation.');
      this.loginCompleted.next(principal);
  }

  updateSessionWithPrincipal(principal: LoggedUser) {
    this.loggerService.debug('Requesting to update Session with the principal.');
    sessionStorage.setItem('principal', JSON.stringify(principal));
    this.setSessionToken(principal.accessToken);
    this.loggerService.debug('Successfully updated Session with the principal.');
  }

  getUser(): string {
    if (sessionStorage.getItem('EMAuserid') != null) {
      return sessionStorage.getItem('EMAuserid')!.toString();
    }
    return "";
  }

  getUserId() {
    return this.getPrincipal().userInfo.userid;
  }

  setUser(user: string, name: string) {
    sessionStorage.setItem('EMAuserid', user);
    sessionStorage.setItem('UserName', name);
    this.userLabelSubject.next(user);
  }

  getUserName() {
    return sessionStorage.getItem('UserName');
  }


  private userLabelSubject = new BehaviorSubject<string>(this.getUser() || '');
  getUserLabelObservable(): BehaviorSubject<string> {
    return this.userLabelSubject;
  }

  getOrganisation(): string {

    if (sessionStorage.getItem('organisationId') != null) {
      return sessionStorage.getItem('organisationId')!.toString();
    }
    return "";

  }

  isDatamaskedUser(): boolean {
    return this.getPrincipal().permissions.includes("EVH DM");
  }

  isEMAOrganisation(): boolean {
    let org = environment.emaOrganisations.find(org => org === this.getOrganisation())
    return org != undefined;
  }

  isNCAOrganisation(): boolean {
    const principal = this.getPrincipal();
    return !!principal && principal.organisation.typeHQ === 2;
  }

  isEMAorNCAOrganisation(): boolean {
    return this.isEMAOrganisation() || this.isNCAOrganisation();
  }

  isAssessmentValidityAllowed(): boolean {
    return this.hasAnyRole("EVH_ASSESSMENT_VALIDITY");
  }

  setOrganisation(organisation: string) {
    sessionStorage.setItem('organisationId', organisation);
    this.organisationLabelSubject.next(organisation);
  }

  private organisationLabelSubject = new BehaviorSubject<string>(this.getOrganisation() || '');
  getOrganisationLabelObservable(): BehaviorSubject<string> {
    return this.organisationLabelSubject;
  }

  getAccessToken(): string {
    return this.getSafeHeaderValue(sessionStorage.getItem('jwtToken'));
  }


  // New method that returns an Observable
  public acquireAndStoreAccessTokenObservable(): Observable<void> {
    this.loggerService.debug('Requesting to acquire and store Access token silently.');
    const account = this.msalService.instance.getAllAccounts()?.[0];
    if (account) {
      this.loggerService.debug(`MFA account found in accounts list of MSAL instance: ${account.username}.`);
      return from(
        this.msalService.instance.acquireTokenSilent({
          scopes: [environment.scope + '/default'],
          account: account
        }).then((response) => {
          if (response.accessToken) {
            this.setAccessToken(response.accessToken);

            if (account &&
              account.idTokenClaims &&
              account.idTokenClaims['EMAuserid'] &&
              account.idTokenClaims['EMAuserid'] !== '' &&
              account.idTokenClaims['EMAuserid'].toString().length > 0
            ) {
              this.setUser(account.idTokenClaims!['EMAuserid']!.toString(),
                account.idTokenClaims!['name']!.toString());
              this.loggerService.debug(`Successfully set Access token and the user information for: ${account.username}. (AuthService.acquireAndStoreAccessTokenObservable)`);
            } else {
              this.setUser('Unknown',
                'Unknown');
              this.loggerService.err('EMAuserid claim not found inside token!');
            }
          }
        }).catch((error) => {
          this.loggerService.err('Error acquiring Access token silently. (AuthService.acquireAndStoreAccessTokenObservable) Error:', error);
        })
      );
    }
    this.loggerService.err('No MFA account found in accounts list of MSAL instance.');
    return of(undefined); // Return an observable that emits undefined if no account is found
  }

  acquireAndStoreAccessToken() {
    this.loggerService.debug('Requesting to acquire and store Access token silently.');
    const account = this.msalService.instance.getAllAccounts()?.[0];
    if (account) {
      this.loggerService.debug(`MFA account found in accounts list of MSAL instance: ${account.username}.`);
      this.msalService.instance.acquireTokenSilent({
        scopes: [environment.scope + '/default'],
        account: account
      }).then((response) => {
        if (response.accessToken) {
          this.setAccessToken(response.accessToken);

          if (account &&
            account.idTokenClaims &&
            account.idTokenClaims['EMAuserid'] &&
            account.idTokenClaims['EMAuserid'] !== '' &&
            account.idTokenClaims['EMAuserid'].toString().length > 0
          ) {
            this.setUser(account.idTokenClaims!['EMAuserid']!.toString(),
              account.idTokenClaims!['name']!.toString());
            this.loggerService.debug(`Successfully set Access token and the user information for: ${account.username}. (AuthService.acquireAndStoreAccessTokenObservable)`);
          } else {
            this.setUser('Unknown',
              'Unknown');
            this.loggerService.err('EMAuserid claim not found inside token!');
          }
        }
      });
    } else {
      this.loggerService.err('No MFA account found in accounts list of MSAL instance.');
    }
  }

  setAccessToken(jwtToken: string) {
    sessionStorage.setItem('jwtToken', this.getSafeHeaderValue(jwtToken));
  }

  getSessionToken(): string {
    return this.getSafeHeaderValue(sessionStorage.getItem('sessionToken'));
  }

  setSessionToken(sessionToken: string) {
    sessionStorage.setItem('sessionToken', this.getSafeHeaderValue(sessionToken));
  }

  private getSafeHeaderValue(value: string | null): string {
    return value && !/[\r\n]/.test(value) ? value : '';
  }

  hasAnyRole(role: string): boolean {
    return this.getPrincipal()?.permissions.includes(role);
  }

  getUserOrganisations(): Observable<any> {
    return this.get(environment.apiUrl + '/api/authorisation/');
  }

  getTermsofUse(organisationType: number): Observable<any> {
    return this.get(environment.apiUrl + '/api/termsOfUse/' + organisationType + '/');
  }

  getUserData(organisation: string): Observable<any> {
    return this.get(environment.apiUrl + '/api/authorisation/' + organisation + '/');
  }

  get(url: string): Observable<any> {
    let headers = new HttpHeaders({ 'Authorization': 'Bearer ' + this.getAccessToken() });
    return this.httpClient.get(url, { headers: headers }).pipe(
      map(resp => {
        return resp;
      })
    );
  }

  showOrgInfo(org_type: string, org_name: string) {
    sessionStorage.setItem("orgType", org_type);
    sessionStorage.setItem("orgName", org_name);
  }

  getOrgType() {
    return sessionStorage.getItem("orgType");
  }

  getOrgName() {
    return sessionStorage.getItem("orgName");
  }

}
